import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { bankingService } from '../../../services/bankingService';

const TransactionEditModal = ({ transaction, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    transactionDate: '',
    description: '',
    amount: '',
    category: '',
    referenceNumber: '',
    reconciliationStatus: 'unreconciled'
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Load category options on component mount
  useEffect(() => {
    // Only load categories if modal is open and user is likely authenticated
    if (isOpen) {
      loadCategoryOptions();
    }
  }, [isOpen]);

  // Initialize form data when transaction changes
  useEffect(() => {
    if (transaction && isOpen) {
      setFormData({
        transactionDate: transaction?.transactionDate || '',
        description: transaction?.description || '',
        amount: transaction?.amount?.toString() || '',
        category: transaction?.category || '',
        referenceNumber: transaction?.referenceNumber || '',
        reconciliationStatus: transaction?.reconciliationStatus || 'unreconciled'
      });
      setError('');
    }
  }, [transaction, isOpen]);

  const loadCategoryOptions = async () => {
    try {
      setLoadingCategories(true);
      setError(''); // Clear any previous errors
      
      // Try to get enhanced category options
      const options = await bankingService?.getEnhancedCategoryOptions();
      setCategoryOptions(options || []);
    } catch (error) {
      console.error('Error loading category options:', error);
      
      // Check if it's an authentication error
      if (error?.message?.includes('Not authenticated') || 
          error?.message?.includes('no active session') ||
          error?.message?.includes('User is not a member')) {
        
        // Set basic fallback categories for unauthenticated users
        setCategoryOptions([
          { value: '', label: 'Select Category', isHeader: false },
          { value: 'customer_payment', label: 'Customer Payment', isHeader: false },
          { value: 'customer_advance', label: 'Customer Advance', isHeader: false },
          { value: 'vendor_payment', label: 'Vendor Payment', isHeader: false },
          { value: 'vendor_advance', label: 'Vendor Advance', isHeader: false },
          { value: 'transfer_to_bank', label: 'Transfer to Another Bank', isHeader: false },
          { value: 'Revenue', label: 'Revenue', isHeader: false },
          { value: 'Office Supplies', label: 'Office Supplies', isHeader: false },
          { value: 'Marketing & Advertising', label: 'Marketing & Advertising', isHeader: false },
          { value: 'Travel & Entertainment', label: 'Travel & Entertainment', isHeader: false },
          { value: 'Utilities', label: 'Utilities', isHeader: false },
          { value: 'Professional Services', label: 'Professional Services', isHeader: false },
          { value: 'Equipment & Software', label: 'Equipment & Software', isHeader: false },
          { value: 'Rent & Facilities', label: 'Rent & Facilities', isHeader: false },
          { value: 'Bank Fees', label: 'Bank Fees', isHeader: false },
          { value: 'Other', label: 'Other', isHeader: false }
        ]);
        
        // Show a subtle warning that enhanced features aren't available
        console.warn('Enhanced category options not available - using basic categories');
      } else {
        // For other errors, still provide basic categories
        setCategoryOptions([
          { value: '', label: 'Select Category', isHeader: false },
          { value: 'Revenue', label: 'Revenue', isHeader: false },
          { value: 'Office Supplies', label: 'Office Supplies', isHeader: false },
          { value: 'Other', label: 'Other', isHeader: false }
        ]);
      }
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData?.transactionDate) {
      setError('Transaction date is required');
      return false;
    }
    if (!formData?.description?.trim()) {
      setError('Description is required');
      return false;
    }
    if (!formData?.amount || isNaN(parseFloat(formData?.amount))) {
      setError('Valid amount is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      const updateData = {
        transactionDate: formData?.transactionDate,
        description: formData?.description?.trim(),
        amount: parseFloat(formData?.amount),
        category: formData?.category || null,
        referenceNumber: formData?.referenceNumber?.trim() || null,
        reconciliationStatus: formData?.reconciliationStatus
      };

      const updatedTransaction = await bankingService?.updateTransaction(
        transaction?.id,
        updateData
      );

      onSave(updatedTransaction);
      onClose();
    } catch (error) {
      console.error('Error updating transaction:', error);
      
      // Provide more specific error messages for authentication issues
      if (error?.message?.includes('Not authenticated') || 
          error?.message?.includes('no active session')) {
        setError('Authentication required. Please log in to update transactions.');
      } else {
        setError(error?.message || 'Failed to update transaction');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const value = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(Math.abs(value));
  };

  const getTransactionTypeColor = (amount) => {
    const value = parseFloat(amount) || 0;
    return value >= 0 ? 'text-success' : 'text-error';
  };

  if (!isOpen || !transaction) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Edit" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Edit Transaction</h3>
              <p className="text-sm text-muted-foreground">
                Update transaction details
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            className="h-8 w-8 p-0"
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span className="text-sm text-error">{error}</span>
            </div>
          )}

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Transaction Date
            </label>
            <Input
              type="date"
              value={formData?.transactionDate}
              onChange={(e) => handleInputChange('transactionDate', e?.target?.value)}
              className="w-full"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <Input
              type="text"
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              placeholder="Enter transaction description"
              className="w-full"
              required
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Amount
            </label>
            <div className="relative">
              <Input
                type="number"
                step="0.01"
                value={formData?.amount}
                onChange={(e) => handleInputChange('amount', e?.target?.value)}
                placeholder="0.00"
                className="w-full pr-20"
                required
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <span className={`text-sm font-medium ${getTransactionTypeColor(formData?.amount)}`}>
                  {formData?.amount ? formatCurrency(formData?.amount) : '$0.00'}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Use positive values for income and negative values for expenses
            </p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Category {loadingCategories && <span className="text-xs text-muted-foreground">(Loading...)</span>}
            </label>
            <select
              value={formData?.category || ''}
              onChange={(e) => handleInputChange('category', e?.target?.value)}
              disabled={loadingCategories}
              className="w-full p-2 border border-border rounded bg-background text-foreground text-sm"
            >
              {categoryOptions?.map((option, index) => {
                if (option?.isHeader) {
                  return (
                    <optgroup key={`header-${index}`} label={option?.label?.replace(/──/g, '')?.trim()}>
                    </optgroup>
                  );
                }
                
                return (
                  <option key={option?.value || index} value={option?.value}>
                    {option?.label}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Reference Number */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reference Number
            </label>
            <Input
              type="text"
              value={formData?.referenceNumber}
              onChange={(e) => handleInputChange('referenceNumber', e?.target?.value)}
              placeholder="Enter reference number (optional)"
              className="w-full"
            />
          </div>

          {/* Reconciliation Status */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reconciliation Status
            </label>
            <select
              value={formData?.reconciliationStatus}
              onChange={(e) => handleInputChange('reconciliationStatus', e?.target?.value)}
              className="w-full p-2 border border-border rounded bg-background text-foreground text-sm"
            >
              <option value="unreconciled">Unreconciled</option>
              <option value="pending">Pending</option>
              <option value="reconciled">Reconciled</option>
            </select>
          </div>

          {/* Current Transaction Info */}
          <div className="p-4 bg-muted/20 rounded-lg border border-border">
            <h4 className="text-sm font-medium text-foreground mb-2">Current Transaction</h4>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p><span className="font-medium">Original Amount:</span> {formatCurrency(transaction?.amount)} </p>
              <p><span className="font-medium">Original Date:</span> {new Date(transaction?.transactionDate)?.toLocaleDateString()}</p>
              <p><span className="font-medium">Bank Account:</span> {transaction?.bankAccountId}</p>
              <p><span className="font-medium">Created:</span> {new Date(transaction?.createdAt)?.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={loading}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {loading ? (
              <>
                <Icon name="Loader" size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Icon name="Save" size={16} className="mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TransactionEditModal;